<template>
  <div class="overflow-x-hidden">
    <div class="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="flex flex-wrap items-center gap-3">
        <div class="relative">
          <input v-model="searchQuery" placeholder="Buscar (nome ou ticker)..." class="form-input pl-10 w-64" />
        </div>

        <div class="flex items-center gap-2 flex-wrap">
          <span class="form-label-inline">Filtrar:</span>
          <button @click="variationFilter = 'all'"
            :class="variationFilter === 'all' ? 'btn-filter-active' : 'btn-filter-inactive'">Todos</button>
          <button @click="variationFilter = 'up'"
            :class="variationFilter === 'up' ? 'bg-green-600 text-white' : 'btn-filter-inactive'">Em Alta</button>
          <button @click="variationFilter = 'down'"
            :class="variationFilter === 'down' ? 'bg-red-600 text-white' : 'btn-filter-inactive'">Em Baixa</button>
          <button @click="showWatchlistOnly = !showWatchlistOnly"
            :class="showWatchlistOnly ? 'bg-amber-500 text-white' : 'btn-filter-inactive'">
            <span class="material-symbols-outlined align-middle mr-1 text-base">star</span> Favoritos
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3 justify-start lg:justify-end">
        <div class="flex items-center gap-2">
          <label for="currency" class="form-label-inline">Moeda:</label>
          <select id="currency" v-model="vsCurrency" class="form-select">
            <option value="usd">USD</option>
            <option value="brl">BRL</option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label for="sort" class="form-label-inline">Ordenar:</label>
          <select id="sort" v-model="sortBy" class="form-select">
            <option value="market_cap">Maior Capitalização</option>
            <option value="volume">Maior Volume (24h)</option>
            <option value="highChange">Maiores Altas (24h)</option>
            <option value="lowChange">Maiores Baixas (24h)</option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <label for="perpage" class="form-label-inline">Itens:</label>
          <select id="perpage" v-model.number="perPage" class="form-select">
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="isLoadingList" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="n in 9" :key="n" class="skeleton-card"></div>
    </div>

    <div v-else-if="errorList" class="col-span-full error-state">
      <p class="font-semibold mb-2">Ocorreu um erro ao carregar os dados.</p>
      <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">Tente novamente ou aguarde alguns instantes.</p>
      <button @click="manualRefresh" class="btn-danger">Tentar novamente</button>
    </div>

    <div v-else>
      <div v-if="pagedCoins.length === 0" class="col-span-full empty-state">
        <p class="mt-4 text-xl font-semibold">Nenhuma criptomoeda encontrada.</p>
      </div>

      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <button v-for="coin in pagedCoins" :key="coin.id" @click="selectCoin(coin)" class="card-crypto group">
          <div>
            <div class="flex items-start justify-between gap-4">
              <div class="flex items-center gap-4 flex-1 min-w-0">
                <img :src="coin.image" :alt="coin.name" class="w-14 h-14 rounded-full shadow-sm flex-shrink-0">
                <div class="flex-1 min-w-0">
                  <p class="font-bold text-lg sm:text-xl text-slate-800 dark:text-slate-100 truncate"
                    :title="coin.name">{{ coin.name }}</p>
                  <p class="text-sm text-slate-500 dark:text-slate-400 uppercase truncate" :title="coin.symbol">{{
                    coin.symbol }}</p>
                </div>
              </div>

              <div class="flex flex-col items-end gap-2">
                <button @click.stop="toggleWatchlist(coin.id)" class="btn-fav"
                  :title="isFav(coin.id) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'">
                  <span class="material-symbols-outlined"
                    :class="isFav(coin.id) ? 'text-amber-500' : 'text-slate-400'">star</span>
                </button>
                <div class="px-2.5 py-1 rounded-full text-xs sm:text-sm font-bold text-right flex-shrink-0"
                  :class="getChangeClass(coin.price_change_percentage_24h)">
                  {{ getChangeSymbol(coin.price_change_percentage_24h) }}
                  {{ Math.abs(coin.price_change_percentage_24h ?? 0).toFixed(2) }}%
                </div>
              </div>
            </div>

            <div v-if="coin.sparkline_in_7d?.price?.length" class="mt-3">
              <svg :viewBox="`0 0 ${sparkW} ${sparkH}`" :width="sparkW" :height="sparkH" class="w-full">
                <path :d="buildSparklinePath(coin.sparkline_in_7d.price)" fill="none" stroke="currentColor"
                  :class="(coin.price_change_percentage_7d_in_currency ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'"
                  stroke-width="2" />
              </svg>
              <div class="flex justify-between text-xs text-slate-400 mt-1">
                <span>7d</span>
                <span
                  :class="(coin.price_change_percentage_7d_in_currency ?? 0) >= 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'">
                  {{ getChangeSymbol(coin.price_change_percentage_7d_in_currency) }}
                  {{ Math.abs(coin.price_change_percentage_7d_in_currency ?? 0).toFixed(2) }}%
                </span>
              </div>
            </div>

            <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700/50 space-y-2 text-sm">
              <div class="grid grid-cols-2 gap-2 items-center text-slate-600 dark:text-slate-300">
                <span>Volume (24h)</span>
                <span class="font-mono font-semibold text-slate-800 dark:text-slate-100 text-right">{{
                  formatBigNumber(coin.total_volume) }}</span>
              </div>
              <div class="grid grid-cols-2 gap-2 items-center text-slate-600 dark:text-slate-300">
                <span>Capitalização</span>
                <span class="font-mono font-semibold text-slate-800 dark:text-slate-100 text-right">{{
                  formatBigNumber(coin.market_cap) }}</span>
              </div>
              <div class="grid grid-cols-2 gap-2 items-center text-slate-600 dark:text-slate-300">
                <span>Rank</span>
                <span class="font-semibold text-slate-800 dark:text-slate-100 truncate text-right">#{{
                  coin.market_cap_rank }}</span>
              </div>
            </div>
          </div>

          <div class="mt-5 text-right">
            <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">Preço Atual</p>
            <p class="font-bold text-2xl sm:text-3xl text-slate-800 dark:text-slate-100">
              <span class="text-xl align-middle">{{ getCurrencySymbol(vsCurrency.toUpperCase()) }}</span>
              {{ formatCurrency(coin.current_price, 2) }}
            </p>
          </div>
        </button>
      </div>

      <div v-if="totalPages > 1" class="mt-8 flex items-center justify-center gap-2">
        <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1"
          class="btn-secondary !px-3 !py-1.5">Anterior</button>
        <span class="text-sm text-slate-600 dark:text-slate-300">Página {{ currentPage }} de {{ totalPages }}</span>
        <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages"
          class="btn-secondary !px-3 !py-1.5">Próxima</button>
      </div>
    </div>

    <transition name="fade-slide-up">
      <div v-if="selectedCoin" @click.self="selectedCoin = null" class="modal-backdrop">
        <div class="modal-container max-w-xl my-8">
          <header class="modal-header">
            <div class="flex items-center gap-4 min-w-0">
              <img :src="selectedCoin.image" :alt="selectedCoin.name"
                class="h-12 w-12 rounded-full shadow-lg flex-shrink-0">
              <div class="flex-1 min-w-0">
                <h2 class="text-xl sm:text-2xl font-bold truncate">{{ selectedCoin.name }}</h2>
                <p class="text-lg text-slate-500 dark:text-slate-400 uppercase">{{ selectedCoin.symbol }}</p>
              </div>
            </div>
            <button @click.stop="selectedCoin = null" class="modal-close-btn">
              <span class="material-symbols-outlined text-xl">close</span>
            </button>
          </header>

          <div class="p-6 lg:p-8 space-y-6">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-2">
              <div class="min-w-0">
                <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Preço Atual</p>
                <p class="text-4xl sm:text-5xl font-bold text-brand-600 dark:text-brand-500 mt-1 break-all">
                  {{ formatCurrency(selectedCoin.current_price, 4) }} {{ vsCurrency.toUpperCase() }}
                </p>
              </div>
              <div class="pt-2">
                <p class="text-lg font-bold text-left sm:text-right"
                  :class="getChangeClass(selectedCoin.price_change_percentage_24h)">
                  {{ getChangeSymbol(selectedCoin.price_change_percentage_24h) }}
                  {{ Math.abs(selectedCoin.price_change_percentage_24h ?? 0).toFixed(2) }}%
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-slate-200 dark:border-slate-700 pt-6">
              <div class="modal-stat-card">
                <span class="modal-stat-label">Capitalização de Mercado</span>
                <p class="modal-stat-value">{{ formatBigNumber(selectedCoin.market_cap) }}</p>
              </div>
              <div class="modal-stat-card">
                <span class="modal-stat-label">Volume (24h)</span>
                <p class="modal-stat-value">{{ formatBigNumber(selectedCoin.total_volume) }}</p>
              </div>
              <div class="modal-stat-card">
                <span class="modal-stat-label">Preço Máx (24h)</span>
                <p class="modal-stat-value text-green-500">{{ formatCurrency(selectedCoin.high_24h) }}</p>
              </div>
              <div class="modal-stat-card">
                <span class="modal-stat-label">Preço Mín (24h)</span>
                <p class="modal-stat-value text-red-500">{{ formatCurrency(selectedCoin.low_24h) }}</p>
              </div>
            </div>

            <div class="pt-2 flex items-center justify-between">
              <a :href="`https://www.coingecko.com/coins/${selectedCoin.id}`" target="_blank"
                class="text-sm text-brand-600 dark:text-brand-500 hover:underline inline-flex items-center gap-1">
                Ver na CoinGecko
                <span class="material-symbols-outlined text-sm">open_in_new</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import axios from 'axios'
const API_BASE_URL = 'https://api.coingecko.com/api/v3'
const coins = ref([])
const selectedCoin = ref(null)
const isLoadingList = ref(true)
const errorList = ref(false)
const lastUpdate = ref('')
const variationFilter = ref('all')
const sortBy = ref('market_cap')
const vsCurrency = ref('usd')
const perPage = ref(50)
const searchQuery = ref('')
const showWatchlistOnly = ref(false)
const watchlist = ref(new Set(JSON.parse(localStorage.getItem('watchlist_ids') || '[]')))
const autoRefresh = ref(true)
const refreshInterval = ref(5)
let timer = null
const sparkW = 320
const sparkH = 60
const sparkPad = 6
const currentPage = ref(1)
const pageSize = computed(() => perPage.value)
const totalPages = computed(() => Math.max(1, Math.ceil(filteredCoins.value.length / pageSize.value)))
const pagedCoins = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filteredCoins.value.slice(start, start + pageSize.value)
})
const filteredCoins = computed(() => {
  let arr = [...coins.value]
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase()
    arr = arr.filter(c => c.name?.toLowerCase().includes(q) || c.symbol?.toLowerCase().includes(q))
  }
  if (variationFilter.value === 'up') {
    arr = arr.filter(c => (c.price_change_percentage_24h ?? 0) > 0)
  } else if (variationFilter.value === 'down') {
    arr = arr.filter(c => (c.price_change_percentage_24h ?? 0) < 0)
  }
  if (showWatchlistOnly.value) {
    arr = arr.filter(c => watchlist.value.has(c.id))
  }
  switch (sortBy.value) {
    case 'market_cap': arr.sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0)); break
    case 'volume': arr.sort((a, b) => (b.total_volume || 0) - (a.total_volume || 0)); break
    case 'highChange': arr.sort((a, b) => (b.price_change_percentage_24h ?? -Infinity) - (a.price_change_percentage_24h ?? -Infinity)); break
    case 'lowChange': arr.sort((a, b) => (a.price_change_percentage_24h ?? Infinity) - (b.price_change_percentage_24h ?? Infinity)); break
    case 'highPrice': arr.sort((a, b) => (b.current_price ?? 0) - (a.current_price ?? 0)); break
    case 'lowPrice': arr.sort((a, b) => (a.current_price ?? 0) - (b.current_price ?? 0)); break
    case 'ticker': arr.sort((a, b) => (a.symbol || '').localeCompare(b.symbol || '')); break
    case 'change1h': arr.sort((a, b) => (b.price_change_percentage_1h_in_currency ?? -Infinity) - (a.price_change_percentage_1h_in_currency ?? -Infinity)); break
    case 'change7d': arr.sort((a, b) => (b.price_change_percentage_7d_in_currency ?? -Infinity) - (a.price_change_percentage_7d_in_currency ?? -Infinity)); break
  }
  return arr
})
const formatCurrency = (value, decimals = 2) => {
  if (typeof value !== 'number') return '--'
  return new Intl.NumberFormat(vsCurrency.value === 'brl' ? 'pt-BR' : 'en-US', { style: 'currency', currency: vsCurrency.value.toUpperCase(), minimumFractionDigits: 2, maximumFractionDigits: decimals, }).format(value)
}
const getCurrencySymbol = (currencyCode) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).formatToParts(0).find(p => p.type === 'currency').value
  } catch { return currencyCode }
}
const formatBigNumber = (num) => {
  if (num == null) return '--'
  const sym = getCurrencySymbol(vsCurrency.value.toUpperCase())
  const n = Math.abs(num)
  if (n >= 1e12) return `${sym}${(num / 1e12).toFixed(2)} T`
  if (n >= 1e9) return `${sym}${(num / 1e9).toFixed(2)} B`
  if (n >= 1e6) return `${sym}${(num / 1e6).toFixed(2)} M`
  return `${sym}${new Intl.NumberFormat(vsCurrency.value === 'brl' ? 'pt-BR' : 'en-US').format(num)}`
}
const getChangeClass = (change) => {
  if (change == null) return 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
  if (change > 0) return 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
  if (change < 0) return 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300'
  return 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
}
const getChangeSymbol = (change) => {
  if (change == null) return ''
  if (change > 0) return '▲'
  if (change < 0) return '▼'
  return ''
}
const buildSparklinePath = (values) => {
  if (!values?.length) return ''
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = (max - min) || 1
  const innerW = sparkW - sparkPad * 2
  const innerH = sparkH - sparkPad * 2
  const pts = values.map((v, i) => {
    const x = sparkPad + (i / (values.length - 1)) * innerW
    const y = sparkPad + innerH - ((v - min) / range) * innerH
    return [x, y]
  })
  return pts.map((p, i) => (i === 0 ? `M ${p[0]},${p[1]}` : `L ${p[0]},${p[1]}`)).join(' ')
}
const toggleWatchlist = (id) => {
  if (watchlist.value.has(id)) watchlist.value.delete(id)
  else watchlist.value.add(id)
  localStorage.setItem('watchlist_ids', JSON.stringify(Array.from(watchlist.value)))
}
const isFav = (id) => watchlist.value.has(id)
const cacheKey = () => `cg_markets_${vsCurrency.value}_${perPage.value}`
const fetchMarketData = async () => {
  isLoadingList.value = true
  errorList.value = false
  const key = cacheKey()
  const cached = localStorage.getItem(key)
  const now = Date.now()
  if (cached) {
    try {
      const { timestamp, data } = JSON.parse(cached)
      if (now - timestamp < 30 * 60 * 1000) {
        coins.value = data
        lastUpdate.value = new Date(timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        isLoadingList.value = false
        return
      }
    } catch {
      //
     }
  }
  try {
    const { data } = await axios.get(`${API_BASE_URL}/coins/markets`, { params: { vs_currency: vsCurrency.value, order: 'market_cap_desc', per_page: perPage.value, page: 1, sparkline: true, price_change_percentage: '1h,24h,7d' } })
    coins.value = data
    localStorage.setItem(key, JSON.stringify({ timestamp: now, data }))
    lastUpdate.value = new Date(now).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    currentPage.value = 1
  } catch (e) {
    console.error('Erro ao buscar dados:', e?.response?.status, e?.message)
    errorList.value = true
  } finally {
    isLoadingList.value = false
  }
}
const manualRefresh = async () => {
  localStorage.removeItem(cacheKey())
  await fetchMarketData()
}
const selectCoin = (coin) => { selectedCoin.value = coin }
let searchT
watch(searchQuery, () => {
  clearTimeout(searchT)
  searchT = setTimeout(() => { currentPage.value = 1 }, 250)
})
watch([vsCurrency, perPage], async () => { await fetchMarketData() })
watch([variationFilter, sortBy, showWatchlistOnly], () => { currentPage.value = 1 })
const setupTimer = () => {
  clearInterval(timer)
  if (autoRefresh.value) {
    timer = setInterval(fetchMarketData, refreshInterval.value * 60 * 1000)
  }
}
watch([autoRefresh, refreshInterval], setupTimer)
const goToPage = (p) => { currentPage.value = Math.min(Math.max(1, p), totalPages.value) }
onMounted(async () => {
  await fetchMarketData()
  setupTimer()
})
onBeforeUnmount(() => clearInterval(timer))
</script>

<style scoped>
.form-input { @apply w-full px-3 py-2 text-sm bg-white dark:bg-brand-800/50 border border-solid border-slate-300 dark:border-slate-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200; }
.form-label-inline { @apply text-sm font-semibold text-slate-600 dark:text-slate-400; }
.form-select { @apply py-1.5 text-sm rounded-lg bg-white dark:bg-brand-800 border-slate-300 dark:border-slate-700 focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm transition; }
button[class*="btn-filter"] { @apply px-3 py-1.5 text-sm font-semibold rounded-full shadow-sm transition-colors flex items-center; }
.btn-filter-inactive { @apply bg-white dark:bg-brand-800 text-slate-700 dark:text-slate-300; }
.btn-filter-active { @apply bg-brand-600 text-white; }
.btn-fav { @apply w-9 h-9 grid place-items-center rounded-full border border-slate-200 dark:border-slate-700 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition; }
.card-crypto { @apply w-full bg-white dark:bg-brand-800 p-4 sm:p-5 rounded-2xl shadow-md border-2 border-transparent flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:border-brand-500/50 text-left; }
.skeleton-card { @apply p-4 sm:p-5 rounded-2xl bg-slate-100 dark:bg-brand-700/50 animate-pulse h-72 shadow-md; }
.empty-state { @apply col-span-full text-center py-16 bg-white dark:bg-brand-800 rounded-2xl text-slate-600 dark:text-slate-300; }
.error-state { @apply col-span-full flex flex-col items-center justify-center text-center text-red-500 bg-white dark:bg-brand-800 rounded-2xl p-8; }
.modal-backdrop { @apply fixed inset-0 z-50 p-4 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto; }
.modal-container { @apply bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full; }
.modal-header { @apply flex items-start justify-between pb-4 border-b border-slate-200 dark:border-slate-700; }
.modal-close-btn { @apply w-10 h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors flex-shrink-0 ml-2; }
.modal-stat-card { @apply bg-slate-100 dark:bg-brand-800/50 p-4 rounded-xl; }
.modal-stat-label { @apply text-sm font-medium text-slate-500 dark:text-slate-400; }
.modal-stat-value { @apply text-lg font-bold text-slate-800 dark:text-slate-100; }
.btn-secondary { @apply px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition disabled:opacity-50 text-slate-700 dark:text-slate-200; }
.btn-danger { @apply px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white transition; }
.material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
.fade-slide-up-enter-active, .fade-slide-up-leave-active { transition: all 0.4s ease; }
.fade-slide-up-enter-from, .fade-slide-up-leave-to { opacity: 0; transform: translateY(20px); }
</style>